import { PetSpeciesID, PetStatusEffect } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import * as Visit from 'src/replicant/visit';
import StateObserver from 'src/StateObserver';
import { fromEntityId, PetEntity } from '../logic/EntityManager';
import { getPetAnimation, getStatusAnimationName } from './appearance.config';
import { Attributes, petActions } from '../pet/pet.redux';
import { isSceneEntered } from 'src/lib/stateUtils';
import { State } from 'src/state';
import { AnimationState, duoAnimationState } from 'src/replicant/pet/PetAssets';
import type { PetController } from '../pet/PetController';
import { getMutex } from 'src/replicant/utils/mutex';
import { SelectedAccessoryState } from 'src/replicant/accessory/accessory.schema';
import { GCInstant as platform } from 'src/lib/gcinstant';
import * as Accessory from 'src/replicant/accessory';
import { tiersAccessoryItemsMap } from 'src/replicant/accessory/accessory.ruleset';
import { AccessorySlot } from 'src/replicant/data/AccessoryItems.schema';
import { getAccessoryPath } from '../accessories/helpers';
import { captureGenericError } from 'src/lib/sentry';
import createIntervalEmitter, {
  IntervalEmitter,
} from 'src/lib/createIntervalEmitter';
import { duration } from 'src/replicant/utils/duration';
import { isArenaAvailable } from 'src/replicant/championsLeague/championsLeague.getters';
import { attachCrownToPet } from '../championsLeague/championsLeague.utils';

export class PetAppearanceController {
  private autoSyncEnabled = getMutex();
  private mutex = getMutex();
  private accessories: SelectedAccessoryState[] = [];

  protected entity: PetEntity;
  private attributesAutoSyncEmitter:
    | IntervalEmitter<Attributes | undefined>
    | undefined;

  constructor(protected pet: PetController) {
    this.entity = pet.entity;
  }

  public init(accessories: SelectedAccessoryState[]) {
    this.accessories = accessories;
    this.attributesAutoSyncEmitter = this.autoSyncPetAttributes();
  }

  public destroy() {
    this.attributesAutoSyncEmitter?.removeAllListeners();
  }

  /**
   * Disable auto sync pet attributes, so UI attributes can be changed without affecting pet from actual attributes
   * It useful when you want to animate pet attributes and want to disable attributes auto sync to avoid race conditions between UI and actual attributes
   */
  public attributesAutoSync(enabled: boolean) {
    if (enabled) {
      this.autoSyncEnabled.release();
    } else {
      this.autoSyncEnabled.lock();
    }
  }

  public lock() {
    this.mutex.lock();
  }

  public unlock() {
    this.mutex.release();
  }

  public isLocked(): boolean {
    return this.mutex.isLocked();
  }

  public setSpecies(species: PetSpeciesID | undefined) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(
      petActions.setSpecies({
        id,
        payload: species,
      }),
    );

    this.updateState();
  }

  public setStatusEffects(
    statusEffects: Partial<Record<PetStatusEffect, boolean>>,
  ) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(
      petActions.setStatusEffects({
        id,
        payload: statusEffects,
      }),
    );

    this.updateState();
  }

  /**
   * Get UI pet attributes
   */
  public getAttributes(): Readonly<Attributes> {
    return StateObserver.getState().pet[this.pet.id].attributes;
  }

  public setAttributes(attributes: Partial<Attributes>) {
    const { id } = this.pet;
    StateObserver.dispatch(
      petActions.setAttributes({ id, payload: attributes }),
    );

    this.updateState();
  }

  public setAction(action: AnimationState | undefined) {
    if (this.pet.destroyed) {
      captureGenericError(
        'Cannot set pet action',
        new Error('PetAppearanceCotroller.setAction() called on destroyed pet'),
        { level: 'warning' },
      );
      return;
    }

    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(petActions.setAction({ id, payload: action }));

    this.updateState();
  }

  public setFlipped(flipX: boolean) {
    this.entity.setFlipped(flipX);
  }

  public getState(state?: State) {
    const { pet, care, user } = state ?? StateObserver.getState();

    const { id } = this.pet;

    if (!pet[id]) return null;

    const { action, statusEffects, species } = pet[id];
    const activeScript = this.pet.conversation.getActiveScript();
    const isVisiting = Visit.getters.isVisiting(user);

    const result = {
      id,
      action,
      species,
      ...statusEffects,
      ...pet[id].attributes,
      activeScript,
      currentCareAction: !!care.currentCareAction,
      isVisiting,
    };

    // Don't show sleep animation based on status effect
    // We use an action animation instead to ensure the pet only sleeps in bed
    result.asleep = false;

    // Use the evolving appearance for the duration of the evolution sequence
    if (this.pet.isEvolving()) {
      result.action = 'evolve';
    }

    // Hide status animation while care action is active
    const hideStatusVfx =
      !(
        isSceneEntered('livingRoom') ||
        isSceneEntered('friendsLivingRoom') ||
        isSceneEntered('arena')
      ) || // Are we in the living room or friend's living room?
      result.currentCareAction || // Is a care action in progress?
      result.action === 'hang' || // Is the pet transitioning scenes?
      result.action === 'evolve' || // Is pet evolving?
      (isSceneEntered('friendsLivingRoom') && user.duo.debut) || // Is pet debuting?
      !this.pet.isFocused; // Is pet stats visible?

    const accessoryHash = JSON.stringify(this.getSelectedAccessories());

    return {
      ...result,
      hideStatusVfx,
      accessoryHash,
    };
  }

  public updateState(state?: ReturnType<PetAppearanceController['getState']>) {
    if (this.mutex.isLocked()) return;

    state = state ?? this.getState();

    if (!state?.species) return;

    this.entity.setSpecies(state.species);
    this.entity.setAnimation(
      getPetAnimation(state, this.entity.moving ? 'walking' : 'idle'),
    );

    const petState = Pet.ruleset.getPetState(state, state, state.isVisiting);
    const statusAnimation = getStatusAnimationName(petState);
    if (statusAnimation) {
      this.entity.setStatusAnimation(statusAnimation);
    }
    this.entity.setStatusAnimationVisible(
      !!statusAnimation && !state.hideStatusVfx,
    );

    // Hide accessories when pet is in bath/toilet/sleep or duo animations.
    const noAccessoriesAnimations = [
      'bath',
      'toilet',
      'sleep',
      ...duoAnimationState,
    ];
    if (noAccessoriesAnimations.includes(state.action!)) {
      this.hideEntityAccessories();
    } else {
      this.updateEntityAccessories();
    }
  }

  private getSelectedAccessories() {
    const { user } = StateObserver.getState();

    return this.pet.id === platform.playerID
      ? Accessory.getters.getSelectedAccessoryState(user)
      : this.accessories;
  }

  private updateEntityAccessories() {
    const equipped = this.getSelectedAccessories().reduce(
      (acc, { id, tierId, slot }) => {
        const { image } = tiersAccessoryItemsMap[id][tierId];
        acc[slot as AccessorySlot] = getAccessoryPath(image, tierId);

        return acc;
      },
      {} as Record<AccessorySlot, string>,
    );

    for (let i = 1; i <= 5; i++) {
      this.entity.setAccessory(
        i as AccessorySlot,
        equipped[i as AccessorySlot],
      );
    }

    // Only the player's pet should have a crown
    if (this.pet.id === platform.playerID) {
      // Attach the crown to pet, only if user is playing champions league
      attachCrownToPet(this.entity.petClip);
    }
  }

  private hideEntityAccessories() {
    for (let i = 1; i <= 5; i++) {
      this.entity.setAccessory(i as AccessorySlot, undefined);
    }
  }

  /**
   * Auto sync pet actual attributes with disabled UI attributes once per second
   */
  private autoSyncPetAttributes() {
    // It works only for local pets, for friends per we have refreshPetAttributes in visit.sequence
    if (!this.pet.isLocalPet()) return undefined;

    const attributesAutoSyncEmitter = createIntervalEmitter(
      ({ user }, now) => {
        if (this.autoSyncEnabled.isLocked()) return undefined;

        const numberAttributes = {
          age: Pet.getters.getPetAge(user, now),
          ...Pet.getters.getAttributeValues(user, now),
        };

        Object.keys(numberAttributes).forEach((rawKey) => {
          const key = rawKey as keyof typeof numberAttributes;
          numberAttributes[key] = Math.round(numberAttributes[key]);
        });

        return {
          ...numberAttributes,
        };
      },
      duration({ seconds: 1 }),
    );

    attributesAutoSyncEmitter.addListener(
      (petState) => petState && this.setAttributes(petState),
    );

    return attributesAutoSyncEmitter as unknown as IntervalEmitter<
      Attributes | undefined
    >;
  }
}
